import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Save, X, Plus } from 'lucide-react';
import { agentsAPI } from '../lib/api';

const AgentSettings = ({ agent, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: agent.name,
    description: agent.description || '',
    system_prompt: agent.system_prompt || 'You are a helpful assistant.',
    model: agent.model || 'gpt-4o',
    temperature: agent.temperature || 0.7,
    is_active: agent.is_active,
    allowed_domains: agent.allowed_domains || [],
    widget_settings: agent.widget_settings || {
      primary_color: '#6366f1',
      position: 'bottom-right',
      greeting: 'Hi! How can I help you today?'
    }
  });
  const [newDomain, setNewDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await agentsAPI.update(agent.agent_id, formData);
      onUpdate({ ...agent, ...updated });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  const addDomain = () => {
    if (newDomain.trim() && !formData.allowed_domains.includes(newDomain.trim())) {
      setFormData({
        ...formData,
        allowed_domains: [...formData.allowed_domains, newDomain.trim()]
      });
      setNewDomain('');
    }
  };

  const removeDomain = (domain) => {
    setFormData({
      ...formData,
      allowed_domains: formData.allowed_domains.filter(d => d !== domain)
    });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Basic Settings */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Basic Settings</CardTitle>
          <CardDescription className="text-slate-400">Configure your agent's identity</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-200">Agent Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-slate-200">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white resize-none"
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-slate-200">Active</Label>
              <p className="text-xs text-slate-400">Enable widget responses</p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {/* AI Configuration */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">AI Configuration</CardTitle>
          <CardDescription className="text-slate-400">Customize AI behavior</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-200">Model</Label>
            <Select
              value={formData.model}
              onValueChange={(value) => setFormData({ ...formData, model: value })}
            >
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="gpt-4o" className="text-white hover:bg-slate-800">GPT-4o</SelectItem>
                <SelectItem value="gpt-4o-mini" className="text-white hover:bg-slate-800">GPT-4o Mini</SelectItem>
                <SelectItem value="gpt-3.5-turbo" className="text-white hover:bg-slate-800">GPT-3.5 Turbo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-200">Temperature</Label>
              <span className="text-sm text-slate-400">{formData.temperature}</span>
            </div>
            <Slider
              value={[formData.temperature]}
              onValueChange={([value]) => setFormData({ ...formData, temperature: value })}
              min={0}
              max={1}
              step={0.1}
              className="py-2"
            />
            <p className="text-xs text-slate-500">Lower = more focused, Higher = more creative</p>
          </div>
        </CardContent>
      </Card>

      {/* System Prompt */}
      <Card className="bg-slate-900/50 border-slate-800 lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-white">System Prompt</CardTitle>
          <CardDescription className="text-slate-400">Define your agent's personality and instructions</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.system_prompt}
            onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white resize-none font-mono text-sm"
            rows={6}
            placeholder="You are a helpful assistant..."
          />
        </CardContent>
      </Card>

      {/* Domain Allowlist */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Domain Allowlist</CardTitle>
          <CardDescription className="text-slate-400">Restrict widget to specific domains (empty = allow all)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              className="bg-slate-800 border-slate-700 text-white"
              onKeyPress={(e) => e.key === 'Enter' && addDomain()}
            />
            <Button onClick={addDomain} size="icon" variant="outline" className="border-slate-700 text-slate-300">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {formData.allowed_domains.map((domain) => (
              <Badge 
                key={domain} 
                variant="secondary" 
                className="bg-slate-800 text-slate-300 hover:bg-slate-700 cursor-pointer group"
                onClick={() => removeDomain(domain)}
              >
                {domain}
                <X className="h-3 w-3 ml-1 opacity-50 group-hover:opacity-100" />
              </Badge>
            ))}
            {formData.allowed_domains.length === 0 && (
              <span className="text-sm text-slate-500">All domains allowed</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Widget Appearance */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Widget Appearance</CardTitle>
          <CardDescription className="text-slate-400">Customize how your widget looks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-200">Primary Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={formData.widget_settings.primary_color}
                onChange={(e) => setFormData({
                  ...formData,
                  widget_settings: { ...formData.widget_settings, primary_color: e.target.value }
                })}
                className="w-12 h-10 p-1 bg-slate-800 border-slate-700 cursor-pointer"
              />
              <Input
                value={formData.widget_settings.primary_color}
                onChange={(e) => setFormData({
                  ...formData,
                  widget_settings: { ...formData.widget_settings, primary_color: e.target.value }
                })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200">Position</Label>
            <Select
              value={formData.widget_settings.position}
              onValueChange={(value) => setFormData({
                ...formData,
                widget_settings: { ...formData.widget_settings, position: value }
              })}
            >
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="bottom-right" className="text-white hover:bg-slate-800">Bottom Right</SelectItem>
                <SelectItem value="bottom-left" className="text-white hover:bg-slate-800">Bottom Left</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200">Greeting Message</Label>
            <Input
              value={formData.widget_settings.greeting}
              onChange={(e) => setFormData({
                ...formData,
                widget_settings: { ...formData.widget_settings, greeting: e.target.value }
              })}
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="lg:col-span-2 flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-500"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AgentSettings;
